/**
 * ZinstopfOptimizationTab - Sensitivity Analysis for Zinstopf Size
 * 
 * This component allows users to compare Monte Carlo simulation results across
 * different anzahlJahreTopf2 (Zinstopf size) configurations to find the optimal value.
 * 
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Icon,
  Input,
  LineChart,
  Popover,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { ZinstopfComparisonConfig, ZinstopfComparisonResult } from '@/types/simulation';
import { CombinedMonteCarloEngine } from '@/services/CombinedMonteCarloEngine';
import { SteuerParameter } from '@/types';

interface ZinstopfOptimizationTabProps {
  steuerParameter: SteuerParameter | null;
  monteCarloEngine: CombinedMonteCarloEngine | undefined;
  iterations: number;
}

export const ZinstopfOptimizationTab: React.FC<ZinstopfOptimizationTabProps> = ({
  steuerParameter,
  monteCarloEngine,
  iterations,
}) => {
  // State management
  const [config, setConfig] = useState<ZinstopfComparisonConfig>({
    minYears: 1,
    maxYears: 10,
    stepSize: 1, // Fixed at 1 year
  });
  const [results, setResults] = useState<ZinstopfComparisonResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentYears, setCurrentYears] = useState<number | undefined>();
  const [totalConfigs, setTotalConfigs] = useState<number | undefined>();
  // excludeExtremes removed - filtering disabled for memory optimization

  // Run the optimization analysis
  const handleRunOptimization = async () => {
    if (!monteCarloEngine || !steuerParameter) {
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setResults([]);

    try {
      const comparisonResults = await monteCarloEngine.runZinstopfSizeComparison(
        iterations,
        config,
        (prog, years, total) => {
          setProgress(Math.round(prog));
          setCurrentYears(years);
          setTotalConfigs(total);
        }
      );

      setResults(comparisonResults);
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsRunning(false);
      setProgress(0);
      setCurrentYears(undefined);
      setTotalConfigs(undefined);
    }
  };

  // Apply client-side filtering to results
  // MEMORY OPTIMIZATION: Filtering disabled - rawResults are empty to save memory
  const filteredResults = useMemo(() => {
    return results;
  }, [results]);

  // Find optimal configuration from filtered results
  const optimalConfig = filteredResults.length > 0
    ? filteredResults.reduce((best, current) =>
        current.statistics.threeTopfSuccessRate > best.statistics.threeTopfSuccessRate
          ? current
          : best
      )
    : null;

  // Calculate failure risk metrics for each configuration
  // MEMORY OPTIMIZATION: Use statistics directly instead of rawResults
  const failureRiskData = useMemo(() => {
    if (!filteredResults || filteredResults.length === 0) {
      return [];
    }

    return filteredResults.map(result => {
      // Use pre-calculated failure metrics from the engine
      const failureRate = result.statistics.failureRate;
      const metrics = result.failureMetrics;
      
      return {
        anzahlJahreTopf2: result.anzahlJahreTopf2,
        failureRate: failureRate,
        avgYearsToFailure: metrics.avgYearsToFailure,
        medianYearsToFailure: metrics.medianYearsToFailure,
        worstCaseYear: metrics.worstCaseYear,
        bestCaseYear: metrics.bestCaseYear,
        totalFailures: metrics.totalFailures,
        totalIterations: metrics.totalIterations,
      };
    });
  }, [filteredResults]);

  // Chart data for success rate visualization (uses filtered results)
  const successRateChartData = filteredResults.length > 0
    ? [
        {
          title: '3-Topf Erfolgsquote',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.anzahlJahreTopf2.toString(), // Use string for categorical x-axis
            y: r.statistics.threeTopfSuccessRate,
          })),
          color: '#0073bb',
        },
        {
          title: '1-Topf Erfolgsquote',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.anzahlJahreTopf2.toString(), // Use string for categorical x-axis
            y: r.statistics.oneTopfSuccessRate,
          })),
          color: '#00a86b',
        },
      ]
    : [];

  // Chart data for average final balance (uses filtered results)
  const balanceChartData = filteredResults.length > 0
    ? [
        {
          title: '3-Topf Ø Endsaldo',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.anzahlJahreTopf2.toString(), // Use string for categorical x-axis
            y: Math.round(r.statistics.threeTopfAvgFinalBalance),
          })),
          color: '#0073bb',
        },
        {
          title: '1-Topf Ø Endsaldo',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.anzahlJahreTopf2.toString(), // Use string for categorical x-axis
            y: Math.round(r.statistics.oneTopfAvgFinalBalance),
          })),
          color: '#00a86b',
        },
      ]
    : [];

  if (!steuerParameter || !monteCarloEngine) {
    return (
      <Box textAlign="center" color="text-body-secondary">
        <p>Bitte führen Sie zuerst eine Monte-Carlo-Simulation aus.</p>
      </Box>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Configuration Panel */}
      <Container
        header={
          <Header
            variant="h2"
            description="Konfigurieren Sie den Bereich der Zinstopf-Größe (Anzahl Jahre Topf 2) für die Sensitivitätsanalyse"
          >
            Optimierungs-Konfiguration
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2}>
            <FormField label="Minimum Jahre">
              <Input
                type="number"
                value={config.minYears.toString()}
                onChange={({ detail }) => setConfig({ ...config, minYears: parseInt(detail.value) || 1 })}
                disabled={isRunning}
              />
            </FormField>
            <FormField label="Maximum Jahre">
              <Input
                type="number"
                value={config.maxYears.toString()}
                onChange={({ detail }) => setConfig({ ...config, maxYears: parseInt(detail.value) || 10 })}
                disabled={isRunning}
              />
            </FormField>
          </ColumnLayout>

          <Box>
            <Button
              variant="primary"
              onClick={handleRunOptimization}
              loading={isRunning}
              disabled={isRunning}
            >
              Optimierung starten ({config.maxYears - config.minYears + 1} Konfigurationen × {iterations} Iterationen)
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      {/* Monte Carlo Analysis Filter */}
      {/* MEMORY OPTIMIZATION: Filter disabled to save memory - rawResults are empty */}
      {/* Filtering is not needed for Zinstopf optimization */}

      <Container>
        <SpaceBetween direction="vertical" size="m">
          {/* Progress Bar */}
          {isRunning && (
            <Box>
              <Box variant="h4">Optimierung läuft...</Box>
              <Box variant="p" color="text-body-secondary">
                {currentYears !== undefined && totalConfigs !== undefined && (
                  <>Konfiguration: {currentYears} Jahre Topf 2 | Fortschritt: {progress}%</>
                )}
              </Box>
              <div
                style={{
                  width: '100%',
                  height: '30px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  overflow: 'hidden',
                  position: 'relative',
                  marginTop: '8px',
                }}
              >
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, progress))}%`,
                    height: '100%',
                    backgroundColor: '#0073bb',
                    transition: 'width 0.3s ease-out',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: progress > 40 ? '#ffffff' : '#374151',
                  }}
                >
                  {progress}%
                </div>
              </div>
            </Box>
          )}
        </SpaceBetween>
      </Container>

      {/* Optimal Configuration Alert */}
      {optimalConfig && (
        <Alert type="success" header="Optimale Konfiguration gefunden">
          <Box>
            <strong>{optimalConfig.anzahlJahreTopf2} Jahre Topf 2</strong> erzielt die beste Erfolgsquote von{' '}
            <strong>{optimalConfig.statistics.threeTopfSuccessRate.toFixed(1)}%</strong> mit einem durchschnittlichen Endsaldo von{' '}
            <strong>{Math.round(optimalConfig.statistics.threeTopfAvgFinalBalance).toLocaleString('de-DE')} €</strong>
          </Box>
        </Alert>
      )}

      {/* Results Table */}
      {filteredResults.length > 0 && (
        <Container
          header={
            <Header variant="h2">
              Vergleichsergebnisse
            </Header>
          }
        >
          <Table
            columnDefinitions={[
              {
                id: 'years',
                header: 'Jahre Topf 2',
                cell: (item: ZinstopfComparisonResult) => (
                  <Box fontWeight={item === optimalConfig ? 'bold' : 'normal'} color={item === optimalConfig ? 'text-status-success' : 'inherit'}>
                    {item.anzahlJahreTopf2}J
                    {item === optimalConfig && ' ★'}
                  </Box>
                ),
                width: 120,
              },
              {
                id: 'successRate',
                header: '3-Topf Erfolgsquote',
                cell: (item: ZinstopfComparisonResult) => `${item.statistics.threeTopfSuccessRate.toFixed(1)}%`,
                width: 150,
              },
              {
                id: 'avgBalance',
                header: 'Ø Endsaldo 3-Topf',
                cell: (item: ZinstopfComparisonResult) => `${Math.round(item.statistics.threeTopfAvgFinalBalance).toLocaleString('de-DE')} €`,
                width: 180,
              },
              {
                id: 'medianBalance',
                header: 'Median Endsaldo 3-Topf',
                cell: (item: ZinstopfComparisonResult) => `${Math.round(item.statistics.threeTopfMedianFinalBalance).toLocaleString('de-DE')} €`,
                width: 200,
              },
              {
                id: 'oneTopfSuccessRate',
                header: '1-Topf Erfolgsquote',
                cell: (item: ZinstopfComparisonResult) => `${item.statistics.oneTopfSuccessRate.toFixed(1)}%`,
                width: 150,
              },
              {
                id: 'avgBalanceOneTopf',
                header: 'Ø Endsaldo 1-Topf',
                cell: (item: ZinstopfComparisonResult) => `${Math.round(item.statistics.oneTopfAvgFinalBalance).toLocaleString('de-DE')} €`,
                width: 180,
              },
              {
                id: 'medianBalanceOneTopf',
                header: 'Median Endsaldo 1-Topf',
                cell: (item: ZinstopfComparisonResult) => `${Math.round(item.statistics.oneTopfMedianFinalBalance).toLocaleString('de-DE')} €`,
                width: 200,
              },
              {
                id: 'advantage',
                header: (
                  <SpaceBetween direction="horizontal" size="xs">
                    <span>3-Topf Vorteil</span>
                    <Popover
                      size="large"
                      position="top"
                      triggerType="custom"
                      dismissButton={false}
                      content={
                        <Box variant="p">
                          Prozentsatz der Monte-Carlo-Iterationen, in denen die 3-Topf-Strategie einen höheren Endsaldo als die 1-Topf-Strategie erreichte.
                          <br /><br />
                          <strong>Interpretation:</strong><br />
                          • 50% = Beide Strategien gleich stark<br />
                          • &gt;50% = 3-Topf überwiegend besser<br />
                          • &lt;50% = 1-Topf überwiegend besser<br />
                          • ≥70% = 3-Topf klar dominant
                        </Box>
                      }
                    >
                      <Icon name="status-info" size="small" />
                    </Popover>
                  </SpaceBetween>
                ),
                cell: (item: ZinstopfComparisonResult) => `${item.statistics.threeTopfAdvantageRate.toFixed(1)}%`,
                width: 130,
              },
            ]}
            items={filteredResults}
            sortingDisabled
            variant="embedded"
          />
        </Container>
      )}

      {/* Failure Risk Analysis Table */}
      {failureRiskData.length > 0 && (
        <Container
          header={
            <Header 
              variant="h2"
              description="Detaillierte Analyse der Versagensrisiken bei Kapitalerschöpfung"
            >
              Failure-Risiko Analyse
            </Header>
          }
        >
          <Table
            columnDefinitions={[
              {
                id: 'years',
                header: 'Jahre Topf 2',
                cell: (item) => {
                  const isOptimal = optimalConfig?.anzahlJahreTopf2 === item.anzahlJahreTopf2;
                  return (
                    <Box fontWeight={isOptimal ? 'bold' : 'normal'} color={isOptimal ? 'text-status-success' : 'inherit'}>
                      {item.anzahlJahreTopf2}J
                      {isOptimal && ' ★'}
                    </Box>
                  );
                },
                width: 120,
                sortingField: 'anzahlJahreTopf2',
              },
              {
                id: 'failureRate',
                header: 'Failure Rate',
                cell: (item) => (
                  <Box color={item.failureRate === 0 ? 'text-status-success' : item.failureRate > 10 ? 'text-status-error' : 'text-status-warning'}>
                    {item.failureRate.toFixed(1)}% ({item.totalFailures}/{item.totalIterations})
                  </Box>
                ),
                width: 150,
                sortingField: 'failureRate',
              },
              {
                id: 'avgYearsToFailure',
                header: 'Ø Jahre bis Versagen',
                cell: (item) => (
                  <Box>
                    {item.avgYearsToFailure > 0 
                      ? `${item.avgYearsToFailure.toFixed(1)} Jahre` 
                      : '-'}
                  </Box>
                ),
                width: 160,
                sortingField: 'avgYearsToFailure',
              },
              {
                id: 'medianYears',
                header: 'Median Jahre bis Versagen',
                cell: (item) => (
                  <Box>
                    {item.medianYearsToFailure > 0
                      ? `${item.medianYearsToFailure.toFixed(1)} Jahre` 
                      : '-'}
                  </Box>
                ),
                width: 180,
                sortingField: 'medianYearsToFailure',
              },
              {
                id: 'worstCase',
                header: 'Frühestes Versagen',
                cell: (item) => (
                  <Box color={item.worstCaseYear > 0 && item.worstCaseYear < 10 ? 'text-status-error' : 'inherit'}>
                    {item.worstCaseYear > 0 
                      ? `Jahr ${item.worstCaseYear}` 
                      : '-'}
                  </Box>
                ),
                width: 150,
                sortingField: 'worstCaseYear',
              },
              {
                id: 'bestCase',
                header: 'Spätestes Versagen',
                cell: (item) => (
                  <Box>
                    {item.bestCaseYear > 0 
                      ? `Jahr ${item.bestCaseYear}` 
                      : '-'}
                  </Box>
                ),
                width: 180,
                sortingField: 'bestCaseYear',
              },
            ]}
            items={failureRiskData}
            sortingDisabled
            variant="embedded"
          />
        </Container>
      )}

      {/* Success Rate Chart */}
      {filteredResults.length > 0 && (
        <Container
          header={
            <Header variant="h2">
              Erfolgsquote nach Zinstopf-Größe
            </Header>
          }
        >
          <LineChart
            series={successRateChartData}
            xScaleType="categorical"
            yDomain={[
              Math.min(...results.flatMap(r => [r.statistics.threeTopfSuccessRate, r.statistics.oneTopfSuccessRate])) - 5,
              100,
            ]}
            xTitle="Anzahl Jahre Topf 2"
            yTitle="Erfolgsquote (%)"
            height={300}
            hideFilter
            ariaLabel="Erfolgsquote Vergleichschart"
            detailPopoverSeriesContent={({ series, y }) => ({
              key: series.title,
              value: `${y.toFixed(1)}%`,
            })}
          />
        </Container>
      )}

      {/* Average Balance Chart */}
      {filteredResults.length > 0 && (
        <Container
          header={
            <Header variant="h2">
              Durchschnittlicher Endsaldo nach Zinstopf-Größe
            </Header>
          }
        >
          <LineChart
            series={balanceChartData}
            xScaleType="categorical"
            yDomain={[
              0,
              Math.max(...results.flatMap(r => [r.statistics.threeTopfAvgFinalBalance, r.statistics.oneTopfAvgFinalBalance])) * 1.1,
            ]}
            xTitle="Anzahl Jahre Topf 2"
            yTitle="Durchschnittlicher Endsaldo (€)"
            height={300}
            hideFilter
            ariaLabel="Endsaldo Vergleichschart"
            detailPopoverSeriesContent={({ series, y }) => ({
              key: series.title,
              value: `${Math.round(y)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`,
            })}
          />
        </Container>
      )}
    </SpaceBetween>
  );
};
